import { ArrowBack, Check, CheckBoxOutlineBlank, Close as CloseIcon, Delete as DeleteIcon, Edit } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTodos } from '../../../context/TodoContext/TodoContext';
import { Todo } from '../../../types/types';
import ConfirmationDialog from '../../../../ConfirmationDialog/ConfirmationDialog';

interface TodoHeaderProps {
    todo: Todo;
    onClose: () => void;
    isCompleted: boolean;
    onComplete: () => void;
    isEditing: boolean;
    setIsEditing: (value: boolean) => void;
}

export const TodoHeader = ({ onClose, isCompleted, onComplete, isEditing, setIsEditing, todo }: TodoHeaderProps) => {
    const { deleteTodo } = useTodos();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const theme = useTheme();

    const handleDelete = () => {
        deleteTodo(todo.id);
        onClose();
    };

    return (
        <Stack position="sticky" top={0} zIndex={1} pt={2} pb={4} gap={2} ml="-16px" bgcolor="background.paper" direction="row" justifyContent="space-between" alignItems="center" minWidth="calc(100% + 16px)">
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton onClick={onClose} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography variant={theme.breakpoints.up('lg') ? "h6" : "subtitle1"}>Todo Details</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Button
                    startIcon={isCompleted ? <Check /> : <CheckBoxOutlineBlank />}
                    onClick={onComplete}
                    color={isCompleted ? 'success' : 'warning'}
                    variant={isCompleted ? 'contained' : 'outlined'}
                    sx={{
                        minWidth: 110,
                        boxShadow: 'none',
                        transition: 'all 0.2s ease-in-out',
                    }}
                    size="small"
                >
                    {isCompleted ? 'Completed' : 'Pending'}
                </Button>
                {isEditing && (
                    <IconButton
                        size="small"
                        onClick={() => setShowDeleteConfirm(true)}
                        sx={{
                            backgroundColor: 'error.main',
                            color: 'error.contrastText',
                            '&:hover': {
                                backgroundColor: 'error.dark',
                                color: 'error.contrastText',
                                transition: 'all 0.1s ease',
                                transform: 'scale(1.2)',
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}

                {!isEditing ?
                    <IconButton
                        size="small"
                        onClick={() => setIsEditing(!isEditing)}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                                transition: 'all 0.1s ease',
                                transform: 'scale(1.15)',
                            }
                        }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    :
                    <IconButton
                        size="small"
                        onClick={() => setIsEditing(!isEditing)}
                        sx={{
                            marginLeft: 0.5,
                            backgroundColor: 'primary.main',
                            border: theme => `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.contrastText',
                            transform: 'scale(1.2)',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                border: theme => `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.contrastText',
                                transition: 'all 0.1s ease',
                                transform: 'scale(1.3)',
                            }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            </Stack>

            <ConfirmationDialog
                open={showDeleteConfirm}
                title="Delete Todo"
                message="Are you sure you want to delete this todo? This action cannot be undone."
                confirmText="Delete"
                severity="error"
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </Stack>
    )
}; 